import { isAxiosError } from 'axios';
import { logError, logInfo, makeLoggableError } from 'lib/util/errors';
import { DateTime } from 'luxon';
import { getRecognitionItems } from './_api';
import {
  RecognitionItem,
  IRecognitionItemPageCollection,
  RecognizePageRequest,
  RecognitionItemPageCollectionProps,
} from './_types';
import { Dispatch, SetStateAction } from 'react';

type RecognitionItemPageRecord = {
  page: number;
  asOf: DateTime;
  items: ReadonlyArray<RecognitionItem>;
};

/**
 * Represents a collection of recognition item pages.
 */
export class RecognitionItemPageCollection implements IRecognitionItemPageCollection {
  /**
   * The timeout duration for a page in milliseconds (10 minutes).
   */
  public static readonly PageTimeout = 10 * 60 * 1000; // 10 minutes

  /**
   * A map to store pages with their respective page numbers and records.
   */
  private readonly _pages: Map<number, RecognitionItemPageRecord> = new Map<
    number,
    RecognitionItemPageRecord
  >();

  /**
   * The total count of recognition items.
   */
  private _count = 0;

  /**
   * The number of items per page.
   */
  private _perPage = 12;

  /**
   * The current page number.
   */
  private _currentPage = -1;

  /**
   * Indicates whether a page is currently loading.
   */
  private _isLoading: boolean;

  /**
   * Function to set the loading state.
   */
  private _setIsLoading: Dispatch<SetStateAction<boolean>>;

  /**
   * Token to cancel the current API request.
   */
  private _cancelToken: AbortController | null = null;

  /**
   * Reference to the HTML component.
   */
  private _componentRef: HTMLDivElement | null = null;

  /**
   * Function to select and load a page.
   */
  public selectPage: (props: { selected: number }) => void;

  /**
   * Constructs a new instance of RecognitionItemPageCollection.
   * @param isLoadingState - The state for loading status.
   */
  public constructor({
    isLoadingState: [isLoading, setIsLoading],
  }: RecognitionItemPageCollectionProps) {
    this._isLoading = isLoading;
    this._setIsLoading = setIsLoading;
    this.selectPage = this.loadPage.bind(this);
  }

  /**
   * Gets the total count of recognition items.
   */
  public get count(): number {
    return this._count;
  }

  /**
   * Gets the current page number.
   */
  public get currentPage(): number {
    return this._currentPage;
  }

  /**
   * Gets the total number of pages.
   */
  public get pageCount(): number {
    return Math.ceil(this._count / this._perPage);
  }

  /**
   * Gets the number of items per page.
   */
  public get perPage(): number {
    return this._perPage;
  }

  /**
   * Sets the number of items per page and reloads the current page.
   * @param value - The number of items per page.
   */
  public set perPage(value: number) {
    this._perPage = value;
    this._pages.clear();
    if (this._currentPage > -1) {
      this.loadPage({ page: this._currentPage });
    }
  }

  /**
   * Sets the reference to the HTML component.
   * @param value - The HTML component reference.
   */
  public set componentRef(value: HTMLDivElement | null) {
    this._componentRef = value;
  }

  /**
   * Gets the reference to the HTML component.
   */
  public get componentRef(): HTMLDivElement | null {
    return this._componentRef;
  }

  /**
   * Loads a page of recognition items.
   * @param props - The properties for the page request.
   * @returns A promise that resolves to an array of recognition items.
   */
  public async loadPage(
    props: Omit<RecognizePageRequest, 'count'> | { selected: number }
  ): Promise<ReadonlyArray<RecognitionItem>> {
    if (!props || this._isLoading) {
      // Gate access so only 1 load request can happen at a time.  Long term
      // update this to cancel the previous request and start a new one.
      return [];
    }
    if ('selected' in props) {
      return this.loadPage({ page: props.selected + 1 });
    }

    const { page } = props;
    const source = `RecognitionItems:get[${page}]`;
    // Try to grab a cached page
    if (this._pages.has(page)) {
      const record = this._pages.get(page);
      if (
        record?.items?.length &&
        record.asOf.plus({ milliseconds: RecognitionItemPageCollection.PageTimeout }) >
          DateTime.now()
      ) {
        this._currentPage = page;
        const event = new CustomEvent('recognitionPageLoaded', {
          detail: { children: record.items, count: this._count },
        });
        (this._componentRef ?? window).dispatchEvent(event);
        return record.items;
      }
    }
    // Otherwise load latest data from API
    this._setIsLoading(true);
    try {
      if (!this._cancelToken) {
        this._cancelToken = new AbortController();
      }
      const response = await getRecognitionItems({
        count: this._perPage,
        page,
        signal: this._cancelToken?.signal,
      });
      if (!response) {
        return [];
      } // This should never happen - probably haven't set up a test
      // If the total count has changed then other pages are invalid
      if (this._count !== response.count) {
        this._count = response.count;
        this._pages.clear();
      }
      // Cache response
      this._pages.set(page, { page, asOf: DateTime.now(), items: response.children });
      this._currentPage = page;

      // Dispatch custom event with new data
      const event = new CustomEvent('recognitionPageLoaded', {
        detail: { children: response.children, count: this._count },
      });
      (this._componentRef ?? window).dispatchEvent(event);

      return response.children;
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        if (e.code === 'ERR_CANCELED') {
          logInfo({ source: source, message: 'Request was canceled' });
        } else {
          logError(source, e, { page, count: this._count });
        }
      } else {
        logError(source, makeLoggableError({ error: e }), { page, count: this._count });
      }
      // If we have an old cached page, us it.  Otherwise, return empty array.
      return this._pages.get(page)?.items ?? [];
    } finally {
      this._setIsLoading(false);
    }
  }

  /**
   * Disposes the collection by aborting any ongoing requests and clearing the pages.
   */
  public dispose(): void {
    this._cancelToken?.abort();
    this._cancelToken = null;
    this._setIsLoading(false);
  }
}
